const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
});

const preciseFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 6,
});

export function decodeCurrency(currency) {
  if (!currency) return 'Unknown';
  const value = String(currency).toUpperCase();
  if (!/^[0-9A-F]{40}$/.test(value)) return value;

  let decoded = '';
  for (let index = 0; index < value.length; index += 2) {
    const byte = Number.parseInt(value.slice(index, index + 2), 16);
    if (byte === 0) break;
    if (byte < 32 || byte > 126) return `${value.slice(0, 8)}…`;
    decoded += String.fromCharCode(byte);
  }
  return decoded || `${value.slice(0, 8)}…`;
}

export function normalizeCurrency(currency) {
  return decodeCurrency(currency).toUpperCase();
}

export function shortAddress(value, head = 6, tail = 5) {
  if (!value) return 'Not public';
  if (value.length <= head + tail + 2) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

export function formatNumber(value, { compact = false } = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  if (numeric === 0) return '0';
  if (Math.abs(numeric) >= 1e18) return numeric.toExponential(2);
  if (compact || Math.abs(numeric) >= 1_000_000) return compactFormatter.format(numeric);
  if (Math.abs(numeric) < 0.000001) return numeric.toExponential(2);
  return preciseFormatter.format(numeric);
}

export function formatLedgerTime(rippleSeconds) {
  if (rippleSeconds === undefined || rippleSeconds === null) return 'Time unavailable';
  const date = new Date((Number(rippleSeconds) + 946684800) * 1000);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatAbsoluteTime(rippleSeconds) {
  if (rippleSeconds === undefined || rippleSeconds === null) return 'Time unavailable';
  const date = new Date((Number(rippleSeconds) + 946684800) * 1000);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

export function rippleToUnixMs(rippleSeconds) {
  if (rippleSeconds === undefined || rippleSeconds === null) return null;
  const ms = (Number(rippleSeconds) + 946684800) * 1000;
  return Number.isFinite(ms) ? ms : null;
}

export function formatRelativeTime(rippleSeconds, now = Date.now()) {
  const ms = rippleToUnixMs(rippleSeconds);
  if (ms === null) return null;
  const seconds = Math.max(0, Math.round((now - ms) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function transactionUrl(hash) {
  return hash ? `https://livenet.xrpl.org/transactions/${hash}` : 'https://livenet.xrpl.org/';
}

export function accountUrl(account) {
  return account ? `https://livenet.xrpl.org/accounts/${account}` : 'https://livenet.xrpl.org/';
}

export function verificationLabel(value) {
  return {
    verified: 'First-party verified',
    corroborated: 'Identity corroborated',
    cataloged: 'RWA.xyz cataloged',
    announced: 'Announced, wallet pending',
    metadata: 'Self-described metadata',
  }[value] || value;
}

export function availabilityLabel(value) {
  return {
    live: 'Live on ledger',
    configured: 'Configured / zero obligations',
    announced: 'Announced',
    legacy: 'Legacy / frozen',
    pilot: 'Pilot / residual state',
    metadata: 'Metadata discovery',
    'cataloged-distributed': 'Cataloged · distributed',
    'cataloged-represented': 'Cataloged · represented',
    cataloged: 'Cataloged · type undisclosed',
  }[value] || value;
}
